import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Collapse,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import BuildIcon from '@material-ui/icons/Build';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { InfoCard, EmptyState } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type {
  DashboardStats,
  CategoryData,
  TopSkillData,
  ScanJob,
  ScanJobList,
  ScanJobDetails,
  ScanJobSkill,
  DeveloperMapping,
  RepoMapping,
} from '../types';

const useStyles = makeStyles(theme => ({
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statValue: {
    fontWeight: 700,
    lineHeight: 1.1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  proficiencyBar: {
    height: 8,
    borderRadius: 4,
    marginTop: 4,
  },
  skillChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
    height: 22,
  },
  timestampCell: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  categoryName: {
    textTransform: 'capitalize',
    fontWeight: 500,
  },
  timelineBar: {
    display: 'flex',
    alignItems: 'flex-end',
    height: 60,
    gap: 2,
    overflow: 'hidden',
  },
  timelineBarItem: {
    flex: 1,
    borderRadius: '2px 2px 0 0',
    minWidth: 2,
    backgroundColor: theme.palette.primary.main,
    opacity: 0.7,
    transition: 'height 0.4s ease',
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  detailRow: {
    backgroundColor: theme.palette.background.default,
  },
  detailContent: {
    padding: theme.spacing(2),
  },
  detailSection: {
    marginBottom: theme.spacing(1.5),
  },
  statusCompleted: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  statusFailed: { backgroundColor: '#ffebee', color: '#c62828' },
  statusPending: { backgroundColor: '#fff8e1', color: '#f57f17' },
  statusInProgress: { backgroundColor: '#e3f2fd', color: '#1565c0' },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
}));

const CATEGORY_COLORS: Record<string, string> = {
  backend: '#3f51b5',
  frontend: '#e91e63',
  devops: '#ff9800',
  mobile: '#4caf50',
  data: '#9c27b0',
  security: '#f44336',
  testing: '#00bcd4',
  architecture: '#795548',
  ai: '#607d8b',
  infrastructure: '#ff5722',
};

function categoryColor(category: string): string {
  return CATEGORY_COLORS[category.toLowerCase()] ?? '#9e9e9e';
}

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  value: number;
  label: string;
}

function StatCard({ icon, iconBg, value, label }: StatCardProps) {
  const classes = useStyles();
  return (
    <Box className={classes.statCard}>
      <Box className={classes.statIcon} style={{ backgroundColor: iconBg }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" className={classes.statValue}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body2" className={classes.statLabel}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Scan Jobs section ────────────────────────────────────────────────────────

interface ScanJobsTableProps {
  api: DevxpApi;
  repoNames: Map<string, string>;
  devNames: Map<string, string>;
}

function ScanJobsTable({ api, repoNames, devNames }: ScanJobsTableProps) {
  const classes = useStyles();
  const [data, setData] = useState<ScanJobList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [jobDetails, setJobDetails] = useState<Map<string, ScanJobDetails>>(new Map());
  const [detailsLoading, setDetailsLoading] = useState<string | null>(null);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getScanJobs(p, pageSize);
      setData(result);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load scan jobs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const handleToggleRow = async (job: ScanJob) => {
    if (expandedJobId === job.id) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(job.id);
    if (!jobDetails.has(job.id)) {
      setDetailsLoading(job.id);
      try {
        const details = await api.getScanJobDetails(job.id);
        setJobDetails(prev => new Map(prev).set(job.id, details));
      } catch {
        // leave details empty — will show fallback
      } finally {
        setDetailsLoading(null);
      }
    }
  };

  const statusChipClass = (status: string) => {
    switch (status) {
      case 'completed': return classes.statusCompleted;
      case 'failed': return classes.statusFailed;
      case 'in_progress': return classes.statusInProgress;
      default: return classes.statusPending;
    }
  };

  const formatTime = (ts: string) =>
    new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <EmptyState missing="data" title="Scan jobs unavailable" description={error} />
    );
  }

  if (!data || data.jobs.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No scan jobs yet"
        description="Scan job history will appear here once the dev-xp-analyzer starts processing repositories."
      />
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 32 }} />
              <TableCell>Repository</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.jobs.map(job => {
              const displayRepo = repoNames.get(job.repositoryName) ?? job.repositoryName;
              const isExpanded = expandedJobId === job.id;
              const details = jobDetails.get(job.id);
              const isLoadingDetails = detailsLoading === job.id;

              return (
                <React.Fragment key={job.id}>
                  <TableRow
                    className={classes.clickableRow}
                    onClick={() => handleToggleRow(job)}
                  >
                    <TableCell padding="none" style={{ paddingLeft: 8 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {displayRepo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.triggerMode}
                        size="small"
                        style={{ fontSize: '0.65rem', height: 18 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status.replace('_', ' ')}
                        size="small"
                        className={statusChipClass(job.status)}
                        style={{ fontSize: '0.65rem', height: 18 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.timestampCell}>
                        {formatTime(job.statusUpdatedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow className={classes.detailRow}>
                    <TableCell colSpan={5} style={{ padding: 0, border: 0 }}>
                      <Collapse in={isExpanded} unmountOnExit>
                        <Box className={classes.detailContent}>
                          {isLoadingDetails ? (
                            <CircularProgress size={20} />
                          ) : details ? (
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" className={classes.detailSection}>
                                  <strong>Extracted Skills ({details.skills.length})</strong>
                                </Typography>
                                <Box mt={0.5}>
                                  {details.skills.length === 0 ? (
                                    <Typography variant="caption" color="textSecondary">No skills extracted</Typography>
                                  ) : (
                                    details.skills.map((skill: ScanJobSkill) => (
                                      <Chip
                                        key={`${skill.skillName}-${skill.category}`}
                                        label={skill.skillName}
                                        size="small"
                                        className={classes.skillChip}
                                        style={{
                                          backgroundColor: `${categoryColor(skill.category)}20`,
                                          color: categoryColor(skill.category),
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" className={classes.detailSection}>
                                  <strong>Developers ({details.developers.length})</strong>
                                </Typography>
                                <Box mt={0.5}>
                                  {details.developers.length === 0 ? (
                                    <Typography variant="caption" color="textSecondary">No developers found</Typography>
                                  ) : (
                                    details.developers.map((userId: string) => (
                                      <Chip
                                        key={userId}
                                        label={devNames.get(userId) ?? userId}
                                        size="small"
                                        className={classes.skillChip}
                                        style={{ fontFamily: devNames.has(userId) ? undefined : 'monospace' }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No details available for this scan job.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.pagination}>
        <Typography variant="caption" color="textSecondary">
          {data.totalCount} scan jobs total
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            Prev
          </Button>
          <Typography variant="caption">{page} / {data.totalPages}</Typography>
          <Button size="small" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

interface AnalyticsDashboardProps {
  api: DevxpApi;
}

export const AnalyticsDashboard = ({ api }: AnalyticsDashboardProps) => {
  const classes = useStyles();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Name mapping tables for unmasking
  const [devNames, setDevNames] = useState<Map<string, string>>(new Map());
  const [repoNames, setRepoNames] = useState<Map<string, string>>(new Map());

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadStats();
    api.getMappings()
      .then(({ mappings }: { mappings: DeveloperMapping[] }) => {
        const m = new Map<string, string>();
        mappings.forEach(dm => m.set(dm.masked_name, dm.real_name));
        setDevNames(m);
      })
      .catch(() => {});
    api.getRepoMappings()
      .then(({ mappings }: { mappings: RepoMapping[] }) => {
        const m = new Map<string, string>();
        mappings.forEach(rm => m.set(rm.masked_name, rm.real_name));
        setRepoNames(m);
      })
      .catch(() => {});
  }, [loadStats, api]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <EmptyState
        missing="data"
        title="Analytics unavailable"
        description={error.includes('not configured')
          ? 'Configure devxp.apiEndpoint, devxp.apiToken, and devxp.projectId in app-config.yaml to enable the analytics dashboard.'
          : error}
      />
    );
  }

  if (!stats) return null;

  const maxCategoryEvents = Math.max(...(stats.categoryBreakdown ?? []).map(c => c.eventCount), 1);
  const maxSkillUsage = Math.max(...(stats.topSkills ?? []).map(s => s.usageCount), 1);
  const maxTimelineCount = Math.max(...(stats.activityTimeline ?? []).map(t => t.eventCount), 1);

  return (
    <Grid container spacing={3}>

      {/* ── Summary stats ──────────────────────────────────────────────── */}
      <Grid item xs={12} sm={6} md={3}>
        <InfoCard title="" noPadding>
          <StatCard
            icon={<PeopleIcon style={{ color: '#fff' }} />}
            iconBg="#3f51b5"
            value={stats.totalUsers}
            label="Developers"
          />
        </InfoCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <InfoCard title="" noPadding>
          <StatCard
            icon={<StorageIcon style={{ color: '#fff' }} />}
            iconBg="#009688"
            value={stats.totalRepos}
            label="Repositories"
          />
        </InfoCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <InfoCard title="" noPadding>
          <StatCard
            icon={<TrendingUpIcon style={{ color: '#fff' }} />}
            iconBg="#ff9800"
            value={stats.totalEvents}
            label="Total Events"
          />
        </InfoCard>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <InfoCard title="" noPadding>
          <StatCard
            icon={<BuildIcon style={{ color: '#fff' }} />}
            iconBg="#9c27b0"
            value={stats.totalSkills}
            label="Unique Skills"
          />
        </InfoCard>
      </Grid>

      {/* ── Activity timeline ──────────────────────────────────────────── */}
      {stats.activityTimeline.length > 0 && (
        <Grid item xs={12} md={8}>
          <InfoCard title="Activity (last 30 days)">
            <Box>
              <Box className={classes.timelineBar}>
                {stats.activityTimeline.map(d => (
                  <Box
                    key={d.date}
                    className={classes.timelineBarItem}
                    title={`${d.date}: ${d.eventCount} events`}
                    style={{ height: `${Math.round((d.eventCount / maxTimelineCount) * 100)}%` }}
                  />
                ))}
              </Box>
              <Box display="flex" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="textSecondary">
                  {stats.activityTimeline[0]?.date}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {stats.activityTimeline[stats.activityTimeline.length - 1]?.date}
                </Typography>
              </Box>
            </Box>
          </InfoCard>
        </Grid>
      )}

      {/* ── Category breakdown ─────────────────────────────────────────── */}
      {stats.categoryBreakdown.length > 0 && (
        <Grid item xs={12} md={stats.activityTimeline.length > 0 ? 4 : 6}>
          <InfoCard title="Skill Categories">
            <Box>
              {stats.categoryBreakdown.slice(0, 8).map((cat: CategoryData) => (
                <Box key={cat.category} mb={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" className={classes.categoryName}>
                      {cat.category}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {cat.eventCount.toLocaleString()} events
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(cat.eventCount / maxCategoryEvents) * 100}
                    className={classes.proficiencyBar}
                    style={{ color: categoryColor(cat.category) }}
                  />
                </Box>
              ))}
            </Box>
          </InfoCard>
        </Grid>
      )}

      {/* ── Top skills ─────────────────────────────────────────────────── */}
      {stats.topSkills.length > 0 && (
        <Grid item xs={12} md={6}>
          <InfoCard title="Top Skills">
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Skill</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Usage</TableCell>
                    <TableCell align="right">Avg score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.topSkills.slice(0, 10).map((skill: TopSkillData) => (
                    <TableRow key={`${skill.skillName}-${skill.category}`}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {skill.skillName}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(skill.usageCount / maxSkillUsage) * 100}
                          className={classes.proficiencyBar}
                          style={{ color: categoryColor(skill.category) }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={skill.category}
                          size="small"
                          className={classes.skillChip}
                          style={{
                            backgroundColor: `${categoryColor(skill.category)}20`,
                            color: categoryColor(skill.category),
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{skill.usageCount.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {(skill.avgProficiency * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        </Grid>
      )}

      {/* ── Scan Jobs History ──────────────────────────────────────────── */}
      <Grid item xs={12} md={stats.topSkills.length > 0 ? 6 : 12}>
        <InfoCard title="Scan Jobs History">
          <ScanJobsTable api={api} repoNames={repoNames} devNames={devNames} />
        </InfoCard>
      </Grid>

      {/* Empty state when there's no data at all */}
      {stats.totalEvents === 0 && (
        <Grid item xs={12}>
          <EmptyState
            missing="data"
            title="No analytics data yet"
            description="Analytics data will appear here once developers start committing code and dev-xp-analyzer processes their contributions."
          />
        </Grid>
      )}
    </Grid>
  );
};
